import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_CONFIG } from "@/config/api";
import { Button } from "@/components/ui/Button";
import { setPortalAuth } from "@/core/auth/storage";
import { portalSurfacePath } from "@/core/auth/portalSurface";
import { isValidLoginCode, normalizeLoginEmail } from "./loginCode";
import type { LoginWorkspace } from "./loginCode";
import { EMAIL_FIELD, LOGIN_BUTTON, LOGIN_ERROR, LOGIN_INPUT, LOGIN_LABEL, LOGIN_PRIMARY, LOGIN_TEXT } from "./loginStyles";
import { WorkspacePickFunnel } from "./WorkspacePickFunnel";

type Stage = "enter-email" | "enter-code" | "pick";

/**
 * Email -> 6-digit code -> portal session. Used as the default login
 * path inside the installed PWA (see loginCode.isStandaloneDisplayMode).
 */
export const LoginCodeForm: React.FC = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("enter-email");
  const [email, setEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [code, setCode] = useState("");
  const [clients, setClients] = useState<LoginWorkspace[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const normalized = normalizeLoginEmail(email);
    if (!normalized) {
      setError("Inserisci un indirizzo email valido.");
      return;
    }
    setIsSubmitting(true);
    try {
      const resp = await fetch(`${API_CONFIG.URL}/portal/auth/request-login-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalized }),
      });
      if (!resp.ok) throw new Error(`status ${resp.status}`);
      setSubmittedEmail(normalized);
      setStage("enter-code");
    } catch {
      setError("Impossibile contattare il server. Riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const storeSessionAndGo = (data: {
    accessToken: string;
    refreshToken: string;
    clientId: string;
    clientName?: string;
    role?: string;
  }) => {
    setPortalAuth(data.clientId, {
      token: data.accessToken,
      refreshToken: data.refreshToken,
      role: data.role || "client",
      clientName: data.clientName,
    });
    navigate(portalSurfacePath(data.role || "client", data.clientId), {
      replace: true,
    });
  };

  const verify = async (chosenClientId?: string) => {
    if (!isValidLoginCode(code)) {
      setError("Il codice deve essere di 6 cifre.");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const body: { email: string; code: string; clientId?: string } = {
        email: submittedEmail,
        code,
      };
      if (chosenClientId) body.clientId = chosenClientId;
      const resp = await fetch(`${API_CONFIG.URL}/portal/auth/verify-login-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        setError("Codice non valido o scaduto. Riprova o richiedi un nuovo codice.");
        return;
      }
      if (data.clients) {
        // DD-026: the funnel asks role first, then client. The choice is
        // never persisted; every login re-asks.
        setClients(data.clients);
        setStage("pick");
        return;
      }
      storeSessionAndGo(data);
    } catch {
      setError("Impossibile contattare il server. Riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetToEmail = () => {
    setStage("enter-email");
    setCode("");
    setClients([]);
    setError(null);
  };

  if (stage === "pick") {
    return (
      <WorkspacePickFunnel
        workspaces={clients}
        onPick={(clientId) => verify(clientId)}
        disabled={isSubmitting}
        error={error}
        footer={
          <Button type="button" variant="secondary" size="lg" className={LOGIN_BUTTON} onClick={resetToEmail}>
            Richiedi un nuovo codice
          </Button>
        }
      />
    );
  }

  if (stage === "enter-code") {
    return (
      <>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            verify();
          }}
          className="space-y-[24px]"
        >
          <p className={LOGIN_TEXT}>
            Abbiamo inviato un codice a{" "}
            <span className="font-medium text-gray-900">{submittedEmail}</span>
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="000000"
            aria-label="Codice di accesso"
            className={`${LOGIN_INPUT} h-[64px] text-center text-[28px] tracking-[0.5em]`}
            disabled={isSubmitting}
          />
          {error && <p className={LOGIN_ERROR}>{error}</p>}
          {/* Never greyed out while the code is short: verify() says what is missing. */}
          <Button type="submit" variant="primary" size="lg" isLoading={isSubmitting} className={LOGIN_PRIMARY}>
            {isSubmitting ? "Verifica in corso..." : "Accedi"}
          </Button>
        </form>
        {/* Outside the form and well clear of "Accedi", so the step back is never tapped by mistake. */}
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className={`${LOGIN_BUTTON} mt-[32px]`}
          onClick={() => {
            setStage("enter-email");
            setCode("");
            setError(null);
          }}
        >
          Usa un'altra email
        </Button>
      </>
    );
  }

  return (
    <form onSubmit={requestCode} className="space-y-[24px]">
      <div>
        <label htmlFor="code-login-email" className={LOGIN_LABEL}>
          Email
        </label>
        <input
          id="code-login-email"
          {...EMAIL_FIELD}
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@esempio.com"
          className={LOGIN_INPUT}
          disabled={isSubmitting}
          required
        />
      </div>
      {error && <p className={LOGIN_ERROR}>{error}</p>}
      <Button type="submit" variant="primary" size="lg" isLoading={isSubmitting} className={LOGIN_PRIMARY}>
        {isSubmitting ? "Invio in corso..." : "Inviami un codice"}
      </Button>
    </form>
  );
};
